import React from "react";

/**
 * [PONPISH] 개인정보 제3자 제공 동의 (React + TailwindCSS)
 * - Tailwind 설정이 된 환경을 전제로 작성합니다.
 * - 데스크톱: 좌측 고정 목차 / 모바일: 접이식 목차
 * - 출처: 사용자가 제공한 원문을 구조화하여 반영
 */

const sections = [
  {
    id: "intro",
    title: "개요",
    body: (
      <p>
        회사는 정보주체의 개인정보를 「개인정보 보호법」 제17조에 따라 정보주체 동의 또는 법률의 특별한 규정이 있는 경우에 한하여 제3자에게 제공합니다. 아래 표는 회사가 개인정보를 제3자에게 제공하는 경우와 그 세부 항목입니다.
      </p>
    ),
  },
  {
    id: "table-reservation",
    title: "온라인 예약 서비스 제공 목적의 제공",
    body: (
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-[720px] w-full text-sm">
          <tbody>
            <tr className="bg-gray-50">
              <th className="w-48 px-4 py-3 text-left font-medium text-gray-600">제공받은 제3자</th>
              <td className="px-4 py-3">제휴 안경원</td>
            </tr>
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공받는 자의 이용목적</th>
              <td className="px-4 py-3">온라인 예약 서비스 제공</td>
            </tr>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공항목</th>
              <td className="px-4 py-3">이름, 휴대번호, 예약일시, 회수일시, 예약상품 및 수량, 도수, 결제 금액</td>
            </tr>
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공받는 자의 보유기간</th>
              <td className="px-4 py-3">온라인 예약 서비스 개시일로부터 15일까지(단, 관련 법령에 따라 보관이 필요한 경우 해당기간까지 보관)</td>
            </tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "table-membership",
    title: "멤버십 포인트 서비스 제공 목적의 제공",
    body: (
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-[720px] w-full text-sm">
          <tbody>
            <tr className="bg-gray-50">
              <th className="w-48 px-4 py-3 text-left font-medium text-gray-600">제공받은 제3자</th>
              <td className="px-4 py-3">제휴 안경원</td>
            </tr>
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공받는 자의 이용목적</th>
              <td className="px-4 py-3">멤버십 포인트 서비스를 위한 본인 식별 인증 및 최적화된 서비스 제공</td>
            </tr>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공항목</th>
              <td className="px-4 py-3">이름, 휴대전화번호, 생년월일, 성별, 예약일, 예약상품 및 수량, 도수, 결제금액, 회원코드, 구매이력, 멤버십 포인트 이력</td>
            </tr>
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">제공받는 자의 보유기간</th>
              <td className="px-4 py-3">동의 철회 또는 회원 탈퇴 시까지</td>
            </tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "refusal",
    title: "동의 거부 권리 및 유의사항",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>회원은 개인정보 제3자 제공 동의를 거부할 권리가 있습니다.</li>
        <li>필수 동의를 거부하는 경우 온라인 예약 서비스, 멤버십 포인트 서비스 등 일부 또는 전체 서비스 이용이 제한될 수 있습니다.</li>
      </ul>
    ),
  },
];

function Toc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <nav className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] overflow-auto pr-6">
      <h2 className="text-sm font-semibold text-gray-600 mb-3">목차</h2>
      <ol className="space-y-2 text-sm">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="block rounded-md px-2 py-1 hover:bg-gray-100 transition"
            >
              {s.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function MobileToc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <details className="lg:hidden mb-4 rounded-xl border bg-white shadow-sm">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">목차 열기</summary>
      <div className="border-t px-4 py-3">
        <ol className="space-y-2 text-sm">
          {sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="block rounded-md px-2 py-1 hover:bg-gray-100">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

export default function PonpishThirdPartyConsentPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 컨테이너 */}
      <main className="mx-auto max-w-6xl px-2 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
          <Toc sections={sections.map(({ id, title }) => ({ id, title }))} />

          <article className="min-w-0">
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <h1 className="text-2xl font-bold tracking-tight">[PONPISH] 개인정보 제3자 제공 동의</h1>
              <p className="mt-2 mb-4 text-sm text-gray-500">시행일: 2025년 8월 1일</p>

              <MobileToc sections={sections.map(({ id, title }) => ({ id, title }))} />

              <div className="divide-y">
                {sections.map((s) => (
                  <section id={s.id} key={s.id} className="scroll-mt-24 py-6">
                    <h2 className="text-lg font-semibold mb-3">{s.title}</h2>
                    <div className="space-y-3 leading-7 text-gray-800">{s.body}</div>
                  </section>
                ))}
              </div>

              <footer className="mt-6 border-t pt-4 text-xs text-gray-500">
                본 문서는 법령·서비스 변경에 따라 갱신될 수 있습니다.
              </footer>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
